import { useState } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { buttonClass, fieldBoxClass } from './CheckoutControls';
import {
  findCoupon,
  formatCents,
  type CheckoutPlan,
  type OrderSummary,
} from '@/lib/checkout/pricing';

interface OrderSummaryCardProps {
  plan: CheckoutPlan;
  order: OrderSummary;
  appliedCoupon: string;
  onApplyCoupon: (code: string) => void;
  onRemoveCoupon: () => void;
}

/**
 * Plan details, itemised pricing and the coupon field. Rendered alongside every
 * checkout step so pricing is always visible.
 */
export default function OrderSummaryCard({
  plan,
  order,
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
}: OrderSummaryCardProps) {
  const [code, setCode] = useState('');
  const [couponError, setCouponError] = useState('');

  const applyCoupon = () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      setCouponError('Enter a coupon code to apply.');
      return;
    }

    const coupon = findCoupon(trimmed);
    if (!coupon) {
      setCouponError('We don’t recognise that code. Checkout will continue at the normal price.');
      return;
    }
    if (coupon.expired) {
      setCouponError('This code has expired. Checkout will continue at the normal price.');
      return;
    }

    setCouponError('');
    setCode('');
    onApplyCoupon(coupon.code);
  };

  const appliedDefinition = appliedCoupon ? findCoupon(appliedCoupon) : undefined;

  return (
    <aside className="w-full lg:max-w-[310px]" aria-label="Order summary">
      <div className="mb-3 overflow-hidden rounded-2xl border border-[#ece6f0] bg-white">
        <div className="bg-[#ff7540] px-5 pb-4 pt-5 text-center">
          <p className="text-[17px] font-extrabold text-white">
            World<span className="font-serif italic font-normal">Via</span>
          </p>
          <p className="mt-2 text-[15px] font-semibold text-white">{plan.name}</p>
        </div>
        <p className="px-5 pt-4 text-xs leading-relaxed text-[#6b5b7d]">{plan.documentsNote}</p>
        <dl className="px-5 pb-4 pt-3">
          {order.lines.map((line) => (
            <div key={line.id} className="flex justify-between gap-3 py-1 text-[12.5px]">
              <dt className="text-[#6b5b7d]">{line.label}</dt>
              <dd
                className={`font-semibold ${line.cents < 0 ? 'text-[#0f7a63]' : 'text-[#3d1751]'}`}
              >
                {formatCents(line.cents)}
              </dd>
            </div>
          ))}
          <div className="mt-3 flex justify-between border-t border-[#ece6f0] pt-3">
            <dt className="text-sm font-bold text-[#3d1751]">Due now</dt>
            <dd className="text-sm font-bold text-[#3d1751]">{formatCents(order.dueNowCents)}</dd>
          </div>
          <p className="mt-2 text-[11px] leading-snug text-[#7a6a8c]">
            Then {formatCents(order.monthlyCents)} per month. Cancel anytime.
          </p>
        </dl>
      </div>

      <div className="rounded-2xl border border-[#ece6f0] bg-white p-4">
        {appliedCoupon ? (
          <div className="flex items-start gap-2">
            <CheckCircle2 aria-hidden="true" className="mt-0.5 h-4 w-4 flex-none text-[#02b899]" />
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] font-semibold text-[#0f7a63]">
                Coupon {appliedCoupon} applied
              </p>
              {appliedDefinition ? (
                <p className="text-[11.5px] text-[#5c4a6e]">{appliedDefinition.label}</p>
              ) : null}
            </div>
            <button type="button" onClick={onRemoveCoupon} className={buttonClass('smGhost')}>
              Remove
            </button>
          </div>
        ) : (
          <>
            <label htmlFor="coupon-code" className="mb-1.5 block text-xs font-semibold text-[#3d1751]">
              Coupon code
            </label>
            <div className="flex items-center gap-2">
              <input
                id="coupon-code"
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                  setCouponError('');
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    applyCoupon();
                  }
                }}
                placeholder="Coupon code"
                aria-invalid={couponError ? true : undefined}
                aria-describedby={couponError ? 'coupon-code-error' : undefined}
                className={`${fieldBoxClass(Boolean(couponError))} h-10 flex-1 text-[12.5px]`}
              />
              <button type="button" onClick={applyCoupon} className={buttonClass('sm')}>
                Apply
              </button>
            </div>
            {couponError ? (
              <p
                id="coupon-code-error"
                role="alert"
                className="mt-2 flex items-start gap-1 text-[11.5px] font-medium leading-snug text-[#dc2626]"
              >
                <AlertCircle aria-hidden="true" className="mt-px h-3.5 w-3.5 flex-none" />
                {couponError}
              </p>
            ) : null}
          </>
        )}
      </div>
    </aside>
  );
}
